import React, { useState, useEffect } from "react";

const Tutorial = ({ onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Check if the device is mobile based on screen width
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Initial check
    checkMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Define the tutorial steps with separate desktop and mobile highlight positions
  const tutorialSteps = [
    {
      title: "Welcome to Links!",
      content:
        "This short tutorial will show you how to use Links to practice math problems.",
      highlight: null,
    },
    {
      title: "Solve the Problem",
      content:
        "Look at the math problem at the top of your screen and solve it in your head or by drawing.",
      highlight: {
        desktop: {
          top: "20px",
          left: "50%",
          width: "200px",
          height: "100px",
          transform: "translateX(-50%)",
        },
        mobile: {
          top: "70px", // Lower on mobile to account for login buttons
          left: "50%",
          width: "30%",
          height: "75px",
          transform: "translateX(-50%)",
        }
      },
    },
    {
      title: "Use the Drawing Tools",
      content:
        "Use the pencil to write or draw. Use the eraser to remove mistakes.",
      highlight: {
        desktop: {
          top: "15px",
          right: "10px",
          width: "190px",
          height: "100px",
          transform: "none",
        },
        mobile: {
          bottom: "90px", // On mobile, drawing tools are at bottom right
          right: "5px",
          width: "200px",
          height: "100px",
          transform: "none",
          top: "auto", // Remove top property for mobile
        }
      },
    },
    {
      title: "Enter Your Answer",
      content: "Type your answer in the input box and click submit.",
      highlight: {
        desktop: {
          bottom: "15px",
          right: "15px",
          width: "275px",
          height: "100px",
          transform: "none",
        },
        mobile: {
          bottom: "15px",
          right: "15px", 
          width: "225px",
          height: "80px",
          transform: "none",
        }
      },
    },
    {
      title: "Get Help from Links",
      content:
        "Need help? Click on the Links avatar in the bottom left corner for a hint!",
      highlight: {
        desktop: {
          bottom: "15px",
          left: "15px",
          width: "160px",
          height: "160px",
          transform: "none",
        },
        mobile: {
          bottom: "15px",
          left: "15px",
          width: "120px", // Smaller on mobile
          height: "120px",
          transform: "none",
        }
      },
    },
  ];

  // Manage navigation between tutorial steps
  const nextStep = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Get the current step data
  const step = tutorialSteps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === tutorialSteps.length - 1;

  // Get the appropriate highlight position based on device type
  const getHighlightStyle = () => {
    if (!step.highlight) return null;
    
    return isMobile 
      ? step.highlight.mobile 
      : step.highlight.desktop;
  };

  // Add a click handler to the backdrop that only closes if clicked directly
  const handleBackdropClick = (e) => {
    // Only close if they clicked the actual backdrop (not the tutorial box)
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Add pulse animation to the highlight
  useEffect(() => {
    // Add the keyframes for the pulse animation
    const style = document.createElement("style");
    style.textContent = `
      @keyframes tutorialPulse {
        0% { box-shadow: 0 0 0 0 rgba(156, 218, 237, 0.7); }
        70% { box-shadow: 0 0 0 15px rgba(156, 218, 237, 0); }
        100% { box-shadow: 0 0 0 0 rgba(156, 218, 237, 0); }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0,0,0,0.5)",
        zIndex: 1000,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {/* Highlight box for the current UI element with responsive styling */}
      {step.highlight && (
        <div
          style={{
            position: "absolute",
            ...getHighlightStyle(),
            border: "3px solid #9CDAED",
            borderRadius: "12px",
            backgroundColor: "rgba(156, 218, 237, 0.15)",
            zIndex: 1001,
            pointerEvents: "none", // Make sure clicks go through
            animation: "tutorialPulse 2s infinite",
            boxShadow: "0 0 0 4px rgba(156, 218, 237, 0.3)",
          }}
        />
      )}

      {/* Info box positioned differently for mobile vs desktop */}
      <div
        style={{
          position: "absolute",
          top: isMobile ? "40%" : "200px", // Higher on mobile
          left: "50%",
          transform: "translateX(-50%)",
          backgroundColor: "#fff",
          padding: isMobile ? "20px" : "25px",
          borderRadius: "16px",
          maxWidth: isMobile ? "85%" : "350px",
          boxShadow: "0 8px 30px rgba(0,0,0,0.3)",
          textAlign: "center",
          fontFamily: "'Inter', sans-serif",
          zIndex: 1002,
        }}
      >
        {/* Close button (X) in the top right */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            background: "transparent",
            border: "none",
            fontSize: "18px",
            fontWeight: "bold",
            cursor: "pointer",
            zIndex: 1003,
            padding: "5px",
            lineHeight: "1",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "25px",
            height: "25px",
            borderRadius: "50%",
            color: "#333",
          }}
          aria-label="Close tutorial"
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#f1f1f1";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          ✕
        </button>

        <h3 style={{ 
          margin: "0 0 15px", 
          color: "#333", 
          fontSize: isMobile ? "20px" : "22px" 
        }}>
          {step.title}
        </h3>

        <p
          style={{
            margin: "0 0 20px",
            color: "#555",
            fontSize: isMobile ? "15px" : "16px",
            lineHeight: "1.4",
          }}
        >
          {step.content}
        </p>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            {!isFirstStep && (
              <button
                onClick={prevStep}
                style={{
                  background: "transparent",
                  border: "1px solid #9CDAED",
                  padding: isMobile ? "6px 12px" : "8px 16px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: isMobile ? "12px" : "14px",
                  fontWeight: "600",
                  color: "#333",
                }}
              >
                Back
              </button>
            )}
          </div>

          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
            {tutorialSteps.map((_, idx) => (
              <div
                key={idx}
                style={{
                  width: isMobile ? "6px" : "8px",
                  height: isMobile ? "6px" : "8px",
                  borderRadius: "50%",
                  backgroundColor: idx === currentStep ? "#9CDAED" : "#ddd",
                }}
              />
            ))}
          </div>

          <div>
            <button
              onClick={nextStep}
              style={{
                background: "#9CDAED",
                border: "none",
                padding: isMobile ? "6px 12px" : "8px 16px",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: isMobile ? "12px" : "14px",
                fontWeight: "600",
                color: "#000",
              }}
            >
              {isLastStep ? "Got it!" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tutorial;
